import type { RemovableRef, StorageLike, UseStorageOptions } from '@vueuse/core';
import { useStorage } from '@vueuse/core';
import type { MaybeRefOrGetter } from 'vue';
import { computed, nextTick, toValue, watch } from 'vue';

function attemptClone<T>(value: T): T {
    try {
        return structuredClone(value);
    } catch (e) {
        console.error('Error cloning default value', e);
        return value;
    }
}

function getDefaultsValue<T>(defaults: MaybeRefOrGetter<T>): T {
    if (typeof defaults === 'object' || typeof defaults === 'function') {
        // Handles pure objects, refs and getter functions
        return attemptClone(toValue(defaults));
    }
    // Handle primitives
    return defaults;
}

export function useDomainStorage<T extends (string | number | boolean | object | null)>(
    key: MaybeRefOrGetter<string>,
    defaults: MaybeRefOrGetter<T>,
    storage: StorageLike = localStorage,
    options?: UseStorageOptions<T> | undefined
): RemovableRef<T> {

    // Will be mutated by useStorage if it's an object
    const initialDefaults = getDefaultsValue(defaults);

    const storageKey = computed(() => `${window.location.hostname}_${toValue(key)}`);
    const storageRef = useStorage<T>(storageKey, initialDefaults, storage, options);

    if (typeof initialDefaults === 'object') {
        watch(storageKey, async (newKey) => {
            // Using flush sync, we can check if the storage is empty before useStorage has the opportunity to create a new item
            if (storage.getItem(newKey) == null) {
                // Waits a tick to make sure that useStorage has loaded the value from storage and updated the ref
                await nextTick();
                storageRef.value = getDefaultsValue(defaults);
            }
        }, {
            flush: 'sync',
        });
    }
    return storageRef;
}
